// InfoPane — the one persistent explain-everything pane. Per-kind
// plain-language renderers (item / activity / quest / skill / creature /
// npc / tile / location) — never a raw JSON dump. Empty selection = tracked
// quest card + a one-line teach. ✕ clears the selection.

import { clear, el, flashClass, kbtn, fmtDur, kbar, tooltip } from '../kit';
import { sendEvent } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { statChip } from '../statvocab';

const num = (v: unknown): string => String(v ?? '?');

export function infoPane(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const titleIcon = el('div', { style: 'width:32px;height:32px;flex:none' });
  const titleLabel = el('div', {
    class: 'k-title',
    style: 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
  });
  const closeBtn = el('button', { class: 'k-btn compact', text: '✕' });
  closeBtn.onclick = () => ui.select('');
  const kindLabel = el('div', { class: 'k-faint k-small' });
  const rows = el('div', {
    class: 'k-scroll',
    style: 'flex:1;min-height:0;display:flex;flex-direction:column;gap:5px',
  });
  const root = el(
    'div',
    { class: 'k-panel info-pane' },
    el('div', { class: 'info-row' }, titleIcon, titleLabel, closeBtn),
    kindLabel,
    rows,
  );

  let lastId = '';

  // ---------- helpers ----------
  const heading = (text: string) =>
    rows.append(el('div', { class: 'k-small k-faint', style: 'margin-top:5px;text-transform:uppercase;letter-spacing:1px', text }));
  const wrapped = (text: string, color = 'var(--text)') =>
    rows.append(el('div', { style: `color:${color};user-select:text`, text }));
  const kv = (k: string, v: string) =>
    rows.append(
      el(
        'div',
        { style: 'display:flex;gap:6px' },
        el('span', { class: 'k-dim', style: 'flex:1', text: k }),
        el('span', { text: v }),
      ),
    );
  const link = (text: string, onClick: () => void) => {
    const l = el('div', { class: 'c-link', text });
    l.addEventListener('click', onClick);
    rows.append(l);
  };
  const card = (title = ''): HTMLElement => {
    const col = el('div', {
      class: 'k-panel raised',
      style: 'padding:6px;display:flex;flex-direction:column;gap:3px',
    });
    if (title) col.append(el('div', { class: 'k-small k-faint', text: title }));
    rows.append(col);
    return col;
  };
  const cardKv = (col: HTMLElement, k: string, v: string) =>
    col.append(
      el(
        'div',
        { style: 'display:flex;gap:6px' },
        el('span', { class: 'k-dim', style: 'flex:1', text: k }),
        el('span', { text: v }),
      ),
    );

  const ctxSlot = (): any => {
    const ctx = ui.selectionCtx;
    if (!ctx) return null;
    const inv = ctx.inv, slot = String(ctx.slot);
    if (inv === 'gear') return vsj.at('equipment')?.[slot];
    let slots: any = null;
    if (inv.startsWith('bag') && !inv.startsWith('bagspace')) slots = vsj.at(`bags.${inv.slice(3)}.slots`);
    else if (inv === 'toolbag') slots = vsj.at('toolbag.slots');
    else if (inv === 'toolbagres') slots = vsj.at('toolbag.res');
    else if (inv === 'ground') slots = vsj.at('ground.slots');
    return slots?.[slot];
  };

  const teachLine = () =>
    rows.append(
      el('div', {
        class: 'k-small k-faint',
        style: 'margin-top:12px',
        text: 'Click anything — an item, a card, a map tile — to learn about it.',
      }),
    );

  const effectiveId = (): string => {
    if (ui.selected.startsWith('tile:')) return vsj.at('tile_card') ? ui.selected : '';
    const infos = vsj.at('infos') ?? {};
    if (ui.selected && infos[ui.selected]) return ui.selected;
    const tracked = vsj.at('quest_bar.tracked');
    if (tracked != null && infos[String(tracked)]) return String(tracked);
    return '';
  };

  // ---------- item ----------
  const itemStatBlocks = (info: any, slot: any = null) => {
    if (slot?.durability && Number(slot.durability.max) > 0) {
      const dura = slot.durability;
      const c = card();
      const frac = Math.max(0, Math.min(1, Number(dura.cur) / Number(dura.max)));
      cardKv(c, 'Durability', `${num(dura.cur)} / ${num(dura.max)}`);
      const bar = kbar(frac < 0.25 ? 'hp' : 'ok', 4);
      c.append(bar.root);
      bar.set(frac);
    } else if (info.durability_max != null) {
      kv('Durability', `${num(info.durability_max)} when new`);
    }
    if (info.requires && typeof info.requires === 'object') {
      // `met` is the DRIVER's verdict — never re-derived from skill levels;
      // the served `consequence` prose replaces the generic line when present
      const met = info.requires.met !== false;
      const cons = info.requires.consequence != null ? ` — ${info.requires.consequence}` : " — you can't use this yet";
      wrapped(
        `Needs ${cap(String(info.requires.skill ?? ''))} Lv ${num(info.requires.level)}` +
          (met ? '' : cons),
        met ? 'var(--text-dim)' : 'var(--danger)',
      );
    }
    if (info.food && typeof info.food === 'object') {
      // secondary restores (fish stamina / root mana / berries both) ride
      // the served food block — render only what's present
      const extra: string[] = [];
      if (info.food.stamina != null) extra.push(`${num(info.food.stamina)} stamina`);
      if (info.food.mana != null) extra.push(`${num(info.food.mana)} mana`);
      const restores = extra.length ? ` Restores ${extra.join(' and ')}.` : '';
      wrapped(`Heals ${num(info.food.heal)} hp over ${num(info.food.eat_s)}s.${restores}`, 'var(--ok)');
    }
    if (info.weapon && typeof info.weapon === 'object') {
      const w = info.weapon;
      const c = card(`Weapon — ${w.class ?? ''}`);
      // full combat block, compact statvocab chips (values server-authored).
      // When the driver serves your_dmg (computed with YOUR stats) it leads,
      // with the served dmg_calc working + the generic equation in the
      // tooltip/faint line; the bare equation stays the fallback.
      const wr = el('div', { class: 'stat-row' });
      if (w.your_dmg != null) {
        const tip = [w.dmg_calc, w.dmg].filter((v: unknown) => v != null).map(String).join(' — ');
        wr.append(statChip('dmg', `${num(w.your_dmg)} ${w.type ?? ''}`.trim(), tip));
      } else {
        wr.append(statChip('dmg', `${w.dmg ?? '?'} ${w.type ?? ''}`.trim()));
      }
      if (w.acc != null) wr.append(statChip('acc', num(w.acc)));
      if (w.swing_s != null) wr.append(statChip('swt', fmtDur(Number(w.swing_s))));
      if (w.range_yd != null) wr.append(statChip('rng', `${num(w.range_yd)} yd`));
      if (w.mana_cost != null) wr.append(statChip('mana', num(w.mana_cost)));
      c.append(wr);
      if (w.your_dmg != null && w.dmg_calc != null)
        c.append(el('div', { class: 'k-small k-faint', text: String(w.dmg_calc) }));
      if (w.offhand_swing_s != null && w.swing_s != null && Math.abs(w.offhand_swing_s - w.swing_s) > 1e-6) {
        const dw = el('div', { class: 'stat-row' },
          el('span', { class: 'k-small k-dim', text: 'dual-wielding' }),
          statChip('swt', fmtDur(Number(w.offhand_swing_s))));
        c.append(dw);
      }
      // served weapon notes (ammo consumption / instant-first / skinning) —
      // rule prose arrives served, rendered verbatim
      if (Array.isArray(w.notes))
        for (const n of w.notes) if (n != null) c.append(el('div', { class: 'k-small k-faint', text: String(n) }));
    }
    if (info.armor && typeof info.armor === 'object') {
      const a = info.armor;
      const c = card(`Armor — worn on ${String(a.slot ?? '?').replace(/_/g, ' ')}`);
      const ar = el('div', { class: 'stat-row' },
        statChip('arm', num(a.armor ?? 0)), statChip('res', num(a.resist ?? 0)));
      if (Number(a.agi ?? 0) !== 0) ar.append(statChip('agi', signed(a.agi)));
      if (Number(a.str ?? 0) !== 0) ar.append(statChip('str', signed(a.str)));
      if (Number(a.dex ?? 0) !== 0) ar.append(statChip('dex', signed(a.dex)));
      c.append(ar);
      // served armor-set line: name + worn count + the full-set bonus, all
      // display composition off served values; green-ish once active
      const set = a.set;
      if (set && typeof set === 'object') {
        const bonus: string[] = [];
        if (set.armor != null) bonus.push(`${signed(set.armor)} arm`);
        if (set.str != null) bonus.push(`${signed(set.str)} str`);
        let line = String(set.name ?? 'Set');
        if (set.pieces_worn != null) line += ` (${set.pieces_worn} worn)`;
        if (bonus.length) line += ` — full set: ${bonus.join(', ')}`;
        c.append(el('div', { class: 'k-small', style: `color:${set.active ? 'var(--ok)' : 'var(--text-dim)'}`, text: line }));
      }
      // served armor notes (shield prose) — verbatim faint lines
      if (Array.isArray(a.notes))
        for (const n of a.notes) if (n != null) c.append(el('div', { class: 'k-small k-faint', text: String(n) }));
    }
    if (info.ammo && typeof info.ammo === 'object') {
      const am = info.ammo;
      const c = card('Ammo');
      cardKv(c, 'Bonus damage', `+${num(am.bonus ?? 0)}`);
      if (am.recover_pct != null)
        cardKv(c, 'Recovery', `${num(am.recover_pct)}% land on the ground after use`);
    }
    if (info.tool && typeof info.tool === 'object') {
      kv('Tool', `T${num(info.tool.tier)} ${info.tool.type ?? ''}`);
      // served usable-from verdict ({skill, level})
      const ua = info.tool.usable_at;
      if (ua && typeof ua === 'object')
        wrapped(`Usable from ${cap(String(ua.skill ?? ''))} Lv ${num(ua.level)}`, 'var(--text-faint)');
    }
    if (info.repair_text != null) wrapped(String(info.repair_text), 'var(--text-faint)');
    if (info.bag_slots != null) kv('Holds', `${num(info.bag_slots)} items`);
    if (info.station && typeof info.station === 'object') {
      kv('Places', `${cap(String(info.station.type ?? '?'))} (T${num(info.station.tier ?? 1)})`);
      if (info.station.effect_text != null) wrapped(String(info.station.effect_text), 'var(--text-faint)');
    }
  };

  const renderUsedIn = (info: any) => {
    const used = info.used_in;
    if (!Array.isArray(used) || !used.length) return;
    heading('Used in');
    let shown = 0;
    let extra = Number(info.used_in_more ?? 0);
    for (const u of used) {
      if (shown >= 3) {
        extra++;
        continue;
      }
      shown++;
      if (u && typeof u === 'object') {
        const tpl = String(u.id ?? '');
        link(`• ${u.name ?? tpl}`, () => {
          ui.select('tpl_' + tpl);
          sendEvent('select_card', { template: tpl });
        });
      } else wrapped(`• ${u}`);
    }
    if (extra > 0) wrapped(`+${extra} more`, 'var(--text-faint)');
  };

  // The action row is SERVED per slot (slot.actions) — the driver decides
  // which actions exist; this renders them verbatim. `split: true` marks the
  // one action needing local count input (the split popover fills mode/n).
  const addActionRow = (slot: any) => {
    const acts = slot?.actions;
    if (!Array.isArray(acts) || !acts.length) return;
    const ctx = ui.selectionCtx!;
    const flow = el('div', { class: 'chip-flow' });
    rows.append(flow);
    for (const a of acts) {
      if (!a || typeof a !== 'object') continue;
      const label = String(a.label ?? '?');
      if (a.split) {
        const sb = el('button', { class: 'k-btn compact', text: label });
        sb.onclick = () => import('./slots').then((m) => m.openSplit(ctx.inv, ctx.slot, sb));
        flow.append(sb);
      } else if (a.danger) {
        flow.append(makeArmed(label, () => sendBtnDict(a.btn)));
      } else {
        flow.append(el('button', { class: 'k-btn compact', text: label, onclick: () => sendBtnDict(a.btn) }));
      }
    }
  };

  const sendBtnDict = (b: any) => {
    if (b && typeof b === 'object' && b._event_id) sendEvent(String(b._event_id), b._req_args ?? {});
  };

  const makeArmed = (label: string, fire: () => void): HTMLButtonElement => {
    let armed = false;
    let timer = 0;
    const b = el('button', { class: 'k-btn compact danger', text: label });
    b.onclick = () => {
      if (!armed) {
        armed = true;
        b.textContent = 'Sure?';
        timer = window.setTimeout(() => {
          armed = false;
          b.textContent = label;
        }, 2500);
        return;
      }
      clearTimeout(timer);
      armed = false;
      b.textContent = label;
      fire();
    };
    return b;
  };

  const renderItem = (id: string, info: any) => {
    let sub = String(info.kind ?? 'item');
    if (info.tier != null) sub += ` · T${num(info.tier)}`;
    kindLabel.textContent = sub;
    const slot = ctxSlot();
    if (ui.selected === id && ui.selectionCtx) addActionRow(slot);
    for (const key of ['description', 'desc']) if (info[key]) wrapped(String(info[key]), 'var(--text-dim)');
    itemStatBlocks(info, slot);
    renderUsedIn(info);
  };

  // ---------- activity ----------
  const renderActivity = (id: string, info: any) => {
    kindLabel.textContent = 'activity';
    let act: any = {};
    if (id.startsWith('tpl_')) act = vsj.at('activities')?.[id.slice(4)] ?? {};

    heading('Makes');
    rows.append(
      el(
        'div',
        { class: 'info-row' },
        icons.plated(String(info.icon ?? 'rock'), 22, 3),
        el('span', { text: String(info.name ?? '?') }),
      ),
    );
    if (act.finds && typeof act.finds === 'object') {
      const names = Object.keys(act.finds)
        .filter((k) => k !== '_nothing_pct')
        .map((k) => vsj.infoName(k));
      if (names.length) wrapped('Can find: ' + names.join(', '), 'var(--text-dim)');
    }
    if (info.out_item != null) {
      const outInfo = vsj.info(String(info.out_item));
      if (outInfo) itemStatBlocks(outInfo);
    }

    heading('Needs');
    const skill = String(info.skill ?? act.skill ?? '');
    if (skill) {
      // `met` is the DRIVER's verdict on the activity's level requirement
      const met = info.met !== false;
      wrapped(
        `${cap(skill)} Lv ${num(info.level ?? act.level ?? 0)}` + (met ? '' : " — you're not there yet"),
        met ? 'var(--text-dim)' : 'var(--danger)',
      );
    }
    if (Array.isArray(act.recipe_row)) {
      for (const ing of act.recipe_row) {
        if (!ing || typeof ing !== 'object') continue;
        const short = ing.short === true; // served verdict
        rows.append(
          el(
            'div',
            { class: 'info-row' },
            icons.texture(String(ing.item ?? ''), 18),
            el('span', {
              class: 'k-small',
              style: `color:${short ? 'var(--count-short)' : 'var(--text-dim)'}`,
              text:
                `${vsj.infoName(String(ing.item ?? ''))} ×${num(ing.count)} (you have ${num(ing.have ?? 0)})` +
                (Number(ing.have_ground ?? 0) > 0 ? ` (+${ing.have_ground} on ground)` : ''),
            }),
          ),
        );
      }
    }
    if (act.tool?.missing) wrapped(`Needs a ${act.tool.type ?? 'tool'} in your toolbag.`, 'var(--danger)');

    heading('Rate');
    const baseS = act.base_s ?? info.base_s;
    const realS = act.real_s ?? info.real_s;
    if (baseS != null) {
      const txt =
        realS != null && Math.abs(Number(realS) - Number(baseS)) > 0.05
          ? `${fmtDur(Number(realS))} (base ${fmtDur(Number(baseS))})`
          : fmtDur(Number(baseS));
      kv('Cycle', txt);
    }
    const xp = info.xp ?? act.xp;
    if (xp != null) kv('XP', `+${num(xp)} per cycle`);
  };

  // ---------- quest / skill / creature / npc / tile ----------
  const renderQuest = (info: any) => {
    kindLabel.textContent = info.main === true ? 'main quest' : 'quest';
    // served shortcut prose (e.g. the kill-anytime shortcut) — highlighted
    if (info.shortcut_text != null)
      rows.append(el('div', { class: 'k-small', style: 'color:var(--c-quest)', text: String(info.shortcut_text) }));
    if (Array.isArray(info.steps)) {
      heading('Steps');
      for (const s of info.steps) {
        if (!s || typeof s !== 'object') continue;
        const done = !!s.done;
        wrapped((done ? '✓  ' : '○  ') + String(s.text ?? ''), done ? 'var(--ok)' : 'var(--text)');
        // served per-step reward prose — small faint line under the step
        if (s.reward_text != null)
          rows.append(el('div', { class: 'k-small k-faint', style: 'padding-left:18px', text: String(s.reward_text) }));
      }
    }
    if (info.claim_reward != null) {
      heading('Claim reward');
      wrapped(String(info.claim_reward));
    }
  };

  const renderSkill = (info: any) => {
    kindLabel.textContent = 'skill';
    if (info.level != null) kv('Level', num(info.level));
    if (info.xp_into_level != null || info.xp_to_next != null)
      kv('XP', `${num(info.xp_into_level ?? 0)} into this level · ${num(info.xp_to_next)} to next`);
    else if (info.xp != null) kv('XP', num(info.xp));
    if (info.trains != null) wrapped(String(info.trains), 'var(--text-dim)');
    if (Array.isArray(info.equations) && info.equations.length) {
      heading('Formulas (live values)');
      for (const e of info.equations) wrapped(String(e), 'var(--text-dim)');
    }
    if (Array.isArray(info.trains_list) && info.trains_list.length) {
      heading('Train it with');
      for (const t of info.trains_list) {
        if (!t || typeof t !== 'object') continue;
        const tid = String(t.id ?? '');
        const tkind = String(t.kind ?? 'craft');
        link(`• Lv ${num(t.level)} — ${t.name ?? tid}`, () => {
          ui.select('tpl_' + tid);
          // "take me there": the view scrolls the card into sight and flashes it
          if (tkind === 'craft') {
            sendEvent('select_card', { template: tid });
            ui.takeThere('crafting', tid);
          } else ui.takeThere('location', tid);
        });
      }
    }
  };

  const renderCreature = (info: any) => {
    kindLabel.textContent = 'creature';
    if (info.tier != null) rows.append(icons.tierBadge(String(info.tier)));
    // the FULL combat stat block, compact statvocab chips — the served
    // dmg_type string rides the dmg chip (like weapon blocks)
    const cr = el('div', { class: 'stat-row' });
    if (info.hp != null) cr.append(statChip('hp', num(info.hp)));
    if (info.dmg != null)
      cr.append(statChip('dmg', `${num(info.dmg)} ${info.dmg_type ?? ''}`.trim()));
    if (info.swing_s != null) cr.append(statChip('swt', `${num(info.swing_s)}s`));
    if (info.acc != null) cr.append(statChip('acc', num(info.acc)));
    if (info.armor != null) cr.append(statChip('arm', num(info.armor)));
    if (info.resist != null) cr.append(statChip('res', num(info.resist)));
    if (info.agi != null) cr.append(statChip('agi', num(info.agi)));
    if (info.speed_yd_s != null) cr.append(statChip('spd', `${num(info.speed_yd_s)} yd/s`));
    if (cr.childElementCount) rows.append(cr);
    if (info.dmg != null && info.swing_s != null)
      wrapped(
        `Hits up to ${num(info.dmg)}${info.dmg_type != null ? ` ${info.dmg_type}` : ''} every ${num(info.swing_s)}s (before your armor).`,
        'var(--text-faint)',
      );
    // vs-you block: every number SERVED (computed against your current gear)
    const vs = info.vs_you;
    if (vs && typeof vs === 'object') {
      heading('Vs you');
      wrapped(
        `Hits you for ~${num(vs.dmg_net)} after your armor (${num(vs.hit_pct)}% to hit) · ` +
          `you hit for ~${num(vs.your_dmg_net)} (${num(vs.your_hit_pct)}%)`,
        'var(--text-dim)',
      );
      // served stat-xp prose (anti-farm scaling note)
      if (vs.stat_xp_note != null)
        rows.append(el('div', { class: 'k-small k-faint', text: String(vs.stat_xp_note) }));
    }
    // xp per kill — served value
    if (info.xp_per_kill != null) {
      const xpc = el('div', { class: 'stat-row' },
        el('span', { class: 'k-chip', style: 'border-color:var(--bar-xp)', text: `+${num(info.xp_per_kill)} xp per kill` }));
      rows.append(xpc);
    }
    // drops table: [{item, name, pct, min, max}] — all served, incl. the odds
    if (Array.isArray(info.drops) && info.drops.length) {
      heading('Drops');
      for (const d of info.drops) {
        if (!d || typeof d !== 'object') continue;
        let range = '';
        if (d.min != null && d.max != null)
          range = String(d.min) === String(d.max) ? `×${d.min}` : `×${d.min}–${d.max}`;
        else if (d.min != null) range = `×${d.min}`;
        rows.append(
          el(
            'div',
            { class: 'info-row' },
            icons.texture(String(d.item ?? ''), 18),
            el('span', {
              class: 'k-small',
              style: 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
              text: String(d.name ?? vsj.infoName(String(d.item ?? ''))),
            }),
            el('span', { class: 'k-small k-dim', text: `${d.pct != null ? `${d.pct}%` : ''}${range ? ` ${range}` : ''}`.trim() }),
          ),
        );
      }
    }
    if (info.drops_note != null)
      rows.append(el('div', { class: 'k-small k-faint', text: String(info.drops_note) }));
  };

  const renderNpc = (info: any) => {
    kindLabel.textContent = 'quest giver';
    if (info.text != null) wrapped(`“${info.text}”`, 'var(--text-dim)');
    if (info.quest_name != null) {
      heading('Offers');
      rows.append(el('div', { class: 'c-quest', text: String(info.quest_name) }));
    }
    if (info.goal != null) wrapped(String(info.goal));
    if (info.reward_text != null) kv('Reward', String(info.reward_text));
  };

  const renderTileInfo = (info: any) => {
    kindLabel.textContent = 'map tile';
    if (info.biome != null) kv('Biome', cap(String(info.biome)));
    if (info.tier != null) kv('Danger tier', `T${num(info.tier)}`);
    for (const key of ['description', 'desc']) if (info[key]) wrapped(String(info[key]), 'var(--text-dim)');
  };

  // ---------- location (map tile from vsj.tile_card) ----------
  const resRows = (title: string, entries: any[], creature = false) => {
    if (!entries.length) return;
    heading(title);
    for (const e of entries) {
      if (!e || typeof e !== 'object') continue;
      const iconKey = String(e.icon ?? e.item ?? '');
      const target = creature ? String(e.info ?? '') : String(e.item ?? '');
      const lbl = el('span', {
        class: creature ? 'c-creature' : 'c-link',
        style: 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap' + (creature ? ';cursor:pointer' : ''),
        text: String(e.name ?? iconKey),
      });
      if (target) lbl.addEventListener('click', () => ui.select(target));
      const row = el('div', { class: 'info-row' }, icons.plated(iconKey, 22, 3), lbl);
      if (creature && e.tier) row.append(icons.tierBadge(String(e.tier)));
      rows.append(row);
    }
  };

  const renderLocation = (tc: any) => {
    if (!tc || typeof tc !== 'object') return;
    const own = Number(tc.dist ?? -1) === 0;
    titleLabel.textContent = tc.name != null ? String(tc.name) : `${tc.x},${tc.y}`;
    titleLabel.style.color = 'var(--text)';
    kindLabel.textContent = 'location';
    titleIcon.append(icons.mapTabGlyph(26));

    let sub = cap(String(tc.biome ?? '?'));
    if (tc.tier != null) sub += ` · danger T${num(tc.tier)}`;
    if (tc.tier_beasts != null) sub += ` · ${tc.tier_beasts}`; // served beast-tier prose
    sub += ' · ' + (own ? 'you are here' : `${num(tc.dist)} tiles away`);
    wrapped(sub, 'var(--text-dim)');

    if (own) {
      const go = el('button', { class: 'k-btn', text: 'Location activities ›' });
      tooltip(go, () => 'Open the Location tab — everything you can do here');
      go.onclick = () => ui.requestView('location');
      rows.append(go);
    } else {
      heading('Travel');
      const walkEta = tc.walk_eta_s;
      const walkBtn = kbtn(tc.walk, {
        label: 'Walk' + (walkEta != null ? ` (${fmtDur(Number(walkEta))})` : ''),
        glow: ui.glows('btn:travel_walk'),
        denyReason: tc.refuse != null ? String(tc.refuse) : null,
      });
      let runTxt = 'Run';
      if (tc.run_eta_s != null) {
        runTxt += ` (${fmtDur(Number(tc.run_eta_s))}`;
        if (tc.run_stamina != null) runTxt += `, −${num(tc.run_stamina)} stam`;
        runTxt += ')';
      }
      const runBtn = kbtn(tc.run, {
        label: runTxt,
        glow: ui.glows('btn:travel_run'),
        denyReason: tc.refuse != null ? String(tc.refuse) : null,
      });
      walkBtn.style.flex = runBtn.style.flex = '1';
      rows.append(el('div', { style: 'display:flex;gap:6px' }, walkBtn, runBtn));
      // served warning: what starting travel would stop (the activity name)
      if (tc.will_stop != null)
        rows.append(el('div', { class: 'k-small', style: 'color:var(--warn)', text: `Starting travel stops: ${tc.will_stop}` }));
      // served travel-xp prose
      if (tc.travel_xp_note != null)
        rows.append(el('div', { class: 'k-small k-faint', text: String(tc.travel_xp_note) }));
    }
    if (tc.refuse != null) wrapped(String(tc.refuse), 'var(--danger)');

    const npc = tc.npc;
    if (npc && typeof npc === 'object') {
      heading('Someone here');
      const nlink = el('span', { class: 'c-link', text: String(npc.name ?? '?') });
      nlink.addEventListener('click', () => ui.select(String(npc.info ?? '')));
      rows.append(el('div', { class: 'info-row' }, icons.plated(String(npc.icon ?? 'npc'), 26, 3), nlink));
      wrapped(`“${npc.text ?? ''}”`, 'var(--text-dim)');
      if (npc.quest && typeof npc.quest === 'object')
        rows.append(el('div', { class: 'c-quest', text: String(npc.quest.name ?? '') }));
      if (npc.accept != null || npc.deny != null)
        rows.append(
          el('div', { style: 'display:flex;gap:6px' },
            kbtn(npc.accept, { label: 'Accept' }),
            kbtn(npc.deny, { label: 'No thanks' })),
        );
      // served prose on what declining means
      if (npc.deny_note != null)
        rows.append(el('div', { class: 'k-small k-faint', text: String(npc.deny_note) }));
    }

    const res = tc.resources;
    if (!res || typeof res !== 'object') return;
    if (Array.isArray(res.gather)) resRows('Gather', res.gather);
    if (res.vein && typeof res.vein === 'object') resRows('Ore vein', [res.vein]);
    if (res.stand && typeof res.stand === 'object') resRows('Tree stand', [res.stand]);
    if (Array.isArray(res.fishing)) resRows('Fishing', res.fishing);
    if (Array.isArray(res.creatures)) resRows('Creatures', res.creatures, true);
  };

  // ---------- fallback ----------
  const renderFallback = (info: any) => {
    for (const key of Object.keys(info)) {
      if (['name', 'icon', 'info_type'].includes(key)) continue;
      const v = info[key];
      if (v == null || typeof v === 'object') continue;
      kv(cap(key), String(v));
    }
  };

  // ---------- refresh ----------
  const refresh = () => {
    const id = effectiveId();
    if (id !== lastId) {
      if (lastId !== '' && id !== '') flashClass(root, 'pop');
      lastId = id;
    }
    clear(rows);
    clear(titleIcon);
    closeBtn.style.display = ui.selected ? '' : 'none';
    if (!id) {
      titleLabel.textContent = 'Nothing selected';
      titleLabel.style.color = 'var(--text)';
      kindLabel.textContent = '';
      teachLine();
      return;
    }
    if (id.startsWith('tile:')) {
      renderLocation(vsj.at('tile_card'));
      return;
    }
    const info = vsj.info(id) ?? {};
    titleLabel.textContent = String(info.name ?? id);
    titleIcon.append(icons.plated(String(info.icon ?? id), 24, 3));
    const kind = String(info.info_type ?? '');
    const kindColors: Record<string, string> = {
      item: 'var(--c-item)', activity: 'var(--c-activity)', skill: 'var(--c-skill)',
      creature: 'var(--c-creature)', quest: 'var(--c-quest)',
    };
    titleLabel.style.color = kindColors[kind] ?? 'var(--text)';
    switch (kind) {
      case 'item': renderItem(id, info); break;
      case 'activity': renderActivity(id, info); break;
      case 'quest': renderQuest(info); break;
      case 'skill': renderSkill(info); break;
      case 'creature': renderCreature(info); break;
      case 'npc': renderNpc(info); break;
      case 'tile': renderTileInfo(info); break;
      default:
        kindLabel.textContent = kind;
        renderFallback(info);
    }
    if (!ui.selected) teachLine();
  };

  ui.onSelect(() => refresh());
  refresh();

  const WATCH = ['infos', 'quest_bar', 'skills', 'bags', 'toolbag', 'ground', 'equipment', 'activities', 'tile_card', 'guidance'];
  return {
    root,
    refresh(changed) {
      if (changed.has('*') || WATCH.some((k) => changed.has(k))) refresh();
    },
  };
}

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function signed(v: unknown): string {
  const n = Number(v ?? 0);
  return (n >= 0 ? '+' : '') + n;
}
